import { useState } from "react";
import { Checkbox, SettingsPanel } from "../../shared";
import { SettingsHeader } from ".";
import { useConfig } from "../../hooks/useConfig";

const LEAD_TIME_OPTIONS = [30, 14, 7, 1];

/**
 * Trucking fleet settings — PM reminders, availability sync, notification channels.
 */
const FleetSettings = () => {
  const { addConfig, configs, appType } = useConfig();
  const [saving, setSaving] = useState(false);

  const availability = configs?.features?.availability || {};
  const maintenanceReminders = configs?.features?.maintenanceReminders || {};
  const channels = maintenanceReminders.channels || {};

  if (appType !== "trucking") {
    return (
      <div>
        <SettingsHeader title="Fleet" />
        <p className="text-sm text-gray-600">
          Fleet settings are available for trucking organizations only.
        </p>
      </div>
    );
  }

  async function patchFeatures(partial) {
    setSaving(true);
    try {
      await addConfig({
        features: {
          ...configs?.features,
          ...partial,
        },
      });
    } finally {
      setSaving(false);
    }
  }

  const availabilityData = [
    {
      title: "Sync availability from active legs",
      subtitle:
        "When enabled, trucks/trailers on non-terminal legs show as unavailable in fleet and dispatch pickers.",
      content: (
        <Checkbox
          id="fleet-auto-sync-legs"
          checked={availability.autoSyncFromLegs !== false}
          disabled={saving}
          label="Enable leg-based availability sync"
          onChange={(target) =>
            patchFeatures({
              availability: {
                ...availability,
                autoSyncFromLegs: target.checked,
              },
            })
          }
        />
      ),
    },
    {
      title: "Sync availability from open maintenance",
      subtitle:
        "Units with open work orders appear unavailable until maintenance is completed.",
      content: (
        <Checkbox
          id="fleet-auto-sync-maintenance"
          checked={availability.autoSyncFromMaintenance !== false}
          disabled={saving}
          label="Enable maintenance-based availability sync"
          onChange={(target) =>
            patchFeatures({
              availability: {
                ...availability,
                autoSyncFromMaintenance: target.checked,
              },
            })
          }
        />
      ),
    },
    {
      title: "Notify assigned driver on leg assign",
      subtitle: "Optional push/in-app alert when equipment is assigned to a leg.",
      content: (
        <Checkbox
          id="fleet-notify-driver"
          checked={Boolean(availability.notifyAssignedDriver)}
          disabled={saving}
          label="Notify assigned driver"
          onChange={(target) =>
            patchFeatures({
              availability: {
                ...availability,
                notifyAssignedDriver: target.checked,
              },
            })
          }
        />
      ),
    },
  ];

  const reminderData = [
    {
      title: "Enable maintenance reminders",
      subtitle: "Daily PM due/overdue alarms for fleet units with schedules.",
      content: (
        <Checkbox
          id="fleet-pm-reminders-enabled"
          checked={maintenanceReminders.enabled !== false}
          disabled={saving}
          label="Send maintenance reminders"
          onChange={(target) =>
            patchFeatures({
              maintenanceReminders: {
                ...maintenanceReminders,
                enabled: target.checked,
              },
            })
          }
        />
      ),
    },
    {
      title: "Lead times (days before due)",
      subtitle: "Select when reminders fire before a scheduled PM date.",
      content: (
        <div className="flex flex-wrap gap-3">
          {LEAD_TIME_OPTIONS.map((day) => {
            const selected = (maintenanceReminders.leadTimeDays || LEAD_TIME_OPTIONS).includes(
              day
            );
            return (
              <Checkbox
                key={day}
                id={`fleet-lead-${day}`}
                label={`${day} ${day === 1 ? "day" : "days"}`}
                checked={selected}
                disabled={saving}
                onChange={(target) => {
                  const current = maintenanceReminders.leadTimeDays || LEAD_TIME_OPTIONS;
                  const next = target.checked
                    ? Array.from(new Set([...current, day])).sort((a, b) => b - a)
                    : current.filter((value) => value !== day);
                  patchFeatures({
                    maintenanceReminders: {
                      ...maintenanceReminders,
                      leadTimeDays: next,
                    },
                  });
                }}
              />
            );
          })}
        </div>
      ),
    },
    {
      title: "In-app notifications",
      subtitle: "Show PM reminders inside the web app.",
      content: (
        <Checkbox
          id="fleet-channel-inapp"
          checked={channels.inApp !== false}
          disabled={saving}
          label="In-app notifications"
          onChange={(target) =>
            patchFeatures({
              maintenanceReminders: {
                ...maintenanceReminders,
                channels: { ...channels, inApp: target.checked },
              },
            })
          }
        />
      ),
    },
    {
      title: "Email notifications",
      subtitle: "Send PM reminders to users with email enabled.",
      content: (
        <Checkbox
          id="fleet-channel-email"
          checked={Boolean(channels.email)}
          disabled={saving}
          label="Email notifications"
          onChange={(target) =>
            patchFeatures({
              maintenanceReminders: {
                ...maintenanceReminders,
                channels: { ...channels, email: target.checked },
              },
            })
          }
        />
      ),
    },
    {
      title: "Push notifications",
      subtitle: "Send PM reminders to mobile devices when push is enabled.",
      content: (
        <Checkbox
          id="fleet-channel-push"
          checked={Boolean(channels.push)}
          disabled={saving}
          label="Push notifications"
          onChange={(target) =>
            patchFeatures({
              maintenanceReminders: {
                ...maintenanceReminders,
                channels: { ...channels, push: target.checked },
              },
            })
          }
        />
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SettingsHeader title="Fleet" />
      <p className="text-sm text-gray-600">
        Configure fleet availability sync and preventive maintenance reminder alarms.
      </p>
      <SettingsPanel title="Availability" data={availabilityData} />
      <SettingsPanel title="Maintenance reminders" data={reminderData} />
    </div>
  );
};

export default FleetSettings;
